import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { useNavigate } from "react-router-dom";

const STATUS_LABEL = {
  new: "Nouveau",
  contacted: "Contacté",
  questionnaire_sent: "Questionnaire envoyé",
  qualified: "Qualifié",
  proposal_sent: "Proposition envoyée",
  proposal_accepted: "Acceptée",
  proposal_declined: "Déclinée",
  closed: "Fermé",
};

export default function Dashboard() {
  const { t, tr } = useI18n();
  const { user, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/appointments/me").then((r) => setAppts(r.data || [])).catch(() => {});
    api.get("/brochures").then((r) => setBrochures(r.data || [])).catch(() => {});
    api.get("/quotes/me").then((r) => setQuotes(r.data || [])).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  if (!user) return <div className="p-16 text-center">{t("common.loading")}</div>;

  return (
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="page-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">{t("dashboard.welcome")}</div>
          <h1 className="serif text-4xl md:text-5xl">{user.name || user.email}</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)]">{user.email}</p>
        </div>
        <button onClick={doLogout} className="gai-btn-outline" data-testid="dashboard-logout">{t("dashboard.logout")}</button>
      </div>

      <div className="gai-hairline my-10" />

      <div className="grid lg:grid-cols-2 gap-10">
        <section className="gai-card gai-sharp p-8" data-testid="dashboard-quotes">
          <div className="overline mb-3">{t("dashboard.myQuotes") || "Mes devis"}</div>
          <h2 className="serif text-2xl mb-4">Mes demandes commerciales</h2>
          {quotes.length === 0 ? (
            <p className="text-sm text-[var(--gai-grey-500)]">{t("dashboard.noItem")}</p>
          ) : (
            <ul className="space-y-4">
              {quotes.map((q) => {
                const link = q.questionnaire_token
                  ? (q.proposal ? `/devis/proposition/${q.questionnaire_token}` : `/devis/questionnaire/${q.questionnaire_token}`)
                  : null;
                return (
                  <li key={q.id} className="border-b border-[var(--gai-grey-200)] pb-4" data-testid={`quote-item-${q.id}`}>
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-medium">{q.service_type}</div>
                        <div className="text-xs text-[var(--gai-grey-500)] mt-1">{q.message?.slice(0, 80)}…</div>
                      </div>
                      <span className="overline text-[var(--gai-red)]">{STATUS_LABEL[q.status] || q.status}</span>
                    </div>
                    {link && (
                      <Link to={link} className="mt-2 inline-flex text-xs text-[var(--gai-red)] underline" data-testid={`quote-link-${q.id}`}>
                        {q.proposal ? "Voir la proposition" : "Compléter le questionnaire"} →
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/devis" className="mt-6 gai-btn-ghost inline-flex" data-testid="dashboard-new-quote">+ Nouvelle demande de devis</Link>
        </section>

        <section className="gai-card gai-sharp p-8" data-testid="dashboard-appointments">
          <div className="overline mb-3">{t("dashboard.myAppointments")}</div>
          <h2 className="serif text-2xl mb-4">{t("nav.appointment")}</h2>
          {appts.length === 0 ? (
            <p className="text-sm text-[var(--gai-grey-500)]">{t("dashboard.noItem")}</p>
          ) : (
            <ul className="space-y-4">
              {appts.map((a) => (
                <li key={a.id} className="border-b border-[var(--gai-grey-200)] pb-3" data-testid={`appt-item-${a.id}`}>
                  <div className="flex justify-between"><span className="font-medium">{a.topic}</span><span className="overline text-[var(--gai-red)]">{a.status}</span></div>
                  <div className="text-sm text-[var(--gai-grey-500)] mt-1">{a.preferred_date}</div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/rendez-vous" className="mt-6 gai-btn-ghost inline-flex" data-testid="dashboard-new-appt">+ Nouvelle demande</Link>
        </section>
      </div>

      <section className="mt-10 gai-card gai-sharp p-8" data-testid="dashboard-brochures">
        <div className="overline mb-3">{t("dashboard.brochures")}</div>
        <h2 className="serif text-2xl mb-4">Ressources GAI</h2>
        {brochures.length === 0 ? (
          <p className="text-sm text-[var(--gai-grey-500)]">{t("dashboard.noItem")}</p>
        ) : (
          <ul className="grid md:grid-cols-2 gap-4">
            {brochures.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-4 p-4 border border-[var(--gai-grey-200)]" data-testid={`brochure-item-${b.id}`}>
                <div>
                  <div className="font-medium">{tr(b.title)}</div>
                  <div className="text-xs text-[var(--gai-grey-500)]">{tr(b.description)}</div>
                </div>
                {b.pdf_url ? (
                  <a href={b.pdf_url} target="_blank" rel="noreferrer" className="gai-btn-outline text-xs">{t("dashboard.download")}</a>
                ) : (
                  <span className="text-[0.7rem] text-[var(--gai-red)]">Réservé</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
